import { Modal, Form, Input, Select, Button, Switch, Avatar, message } from "antd";
import { UserOutlined, CameraOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface StaffFormValues {
    name: string;
    cms_id?: string;
    email?: string;
    phone?: string;
    position?: string;
    department_id?: number | null;
    role_id?: number | null;
    is_active?: boolean;
    email_user?: string;
    password_user?: string;
    avatar?: File;
}

interface Props {
    open: boolean;
    mode: "add" | "edit";
    initialValues?: StaffFormValues;
    initialAvatar?: string;
    departments: { id: number; name: string }[];
    roles?: { id: number; name: string }[];
    onCancel: () => void;
    onSubmit: (values: StaffFormValues) => void;
    loading?: boolean;
}

const MAX_AVATAR_SIZE = 5 * 1024 * 1024; // 5MB

export default function AddEmployeeModal({
    open,
    mode,
    initialValues,
    initialAvatar,
    departments,
    roles = [],
    onCancel,
    onSubmit,
    loading,
}: Props) {
    const [form] = Form.useForm();
    const { t } = useTranslation();

    const [avatarFile, setAvatarFile] = useState<File>();
    const [avatarPreview, setAvatarPreview] = useState<string>();
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (open) {
            form.setFieldsValue(
                initialValues || {
                    name: "",
                    cms_id: "",
                    email: "",
                    phone: "",
                    position: "",
                    department_id: null,
                    role_id: null,
                    is_active: true,
                    email_user: "",
                    password_user: "",
                }
            );
            setAvatarFile(undefined);
            setAvatarPreview(initialAvatar || undefined);
        }
    }, [open, initialValues, initialAvatar, form]);

    const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // cho phép chọn lại cùng một file
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            message.error(t("employee.avatar_invalid_type"));
            return;
        }
        if (file.size > MAX_AVATAR_SIZE) {
            message.error(t("employee.avatar_too_large"));
            return;
        }

        setAvatarFile(file);
        setAvatarPreview(URL.createObjectURL(file));
    };

    const handleFinish = (values: StaffFormValues) => {
        onSubmit({ ...values, ...(avatarFile ? { avatar: avatarFile } : {}) });
    };

    return (
        <Modal
            title={mode === "add" ? t('employee.modal_add_title') : t('employee.modal_edit_title')}
            open={open}
            width={720}
            onCancel={() => {
                form.resetFields();
                onCancel();
            }}
            footer={null}
            centered
            destroyOnClose
            className="dark:[&_.ant-modal-content]:bg-[#1f1f1f]
                dark:[&_.ant-modal-header]:bg-[#1f1f1f]
                dark:[&_.ant-modal-close]:text-white
                dark:[&_.ant-modal-title]:text-white"
        >
            <Form form={form} layout="vertical" onFinish={handleFinish}>
                <Form.Item label={<span className="t-text-white">{t('employee.label_avatar')}</span>}>
                    <div className="flex items-center gap-4">
                        <div
                            className="relative group cursor-pointer rounded-full overflow-hidden shrink-0"
                            style={{ width: 72, height: 72 }}
                            onClick={() => fileInputRef.current?.click()}
                            title={t('employee.label_avatar')}
                        >
                            <Avatar size={72} src={avatarPreview} icon={<UserOutlined />} />
                            <div className="absolute inset-0 bg-black/45 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                                <CameraOutlined style={{ fontSize: 20 }} />
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: "none" }}
                                onChange={handleAvatarChange}
                            />
                        </div>
                        <span className="text-sm text-gray-500 t-text-white">
                            {t('employee.avatar_hint')}
                        </span>
                    </div>
                </Form.Item>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                <Form.Item
                    label={<span className="t-text-white">{t('employee.label_name')}</span>}
                    name="name"
                    rules={[{ required: true, message: t('employee.name_required') }]}
                >
                    <Input
                        placeholder={t('employee.placeholder_name')}
                        className="dark:bg-[#141414] t-text-white dark:border-gray-600"
                    />
                </Form.Item>

                <Form.Item
                    label={<span className="t-text-white">{t('employee.label_cms_id')}</span>}
                    name="cms_id"
                >
                    <Input
                        placeholder={t('employee.placeholder_cms_id')}
                        className="dark:bg-[#141414] t-text-white dark:border-gray-600"
                    />
                </Form.Item>

                <Form.Item
                    label={<span className="t-text-white">{t('employee.label_email')}</span>}
                    name="email"
                >
                    <Input
                        placeholder={t('employee.placeholder_email')}
                        className="dark:bg-[#141414] t-text-white dark:border-gray-600"
                    />
                </Form.Item>

                <Form.Item
                    label={<span className="t-text-white">{t('employee.label_phone')}</span>}
                    name="phone"
                >
                    <Input
                        placeholder={t('employee.placeholder_phone')}
                        className="dark:bg-[#141414] t-text-white dark:border-gray-600"
                    />
                </Form.Item>

                <Form.Item
                    label={<span className="t-text-white">{t('employee.label_position')}</span>}
                    name="position"
                >
                    <Input
                        placeholder={t('employee.placeholder_position')}
                        className="dark:bg-[#141414] t-text-white dark:border-gray-600"
                    />
                </Form.Item>

                <Form.Item
                    label={<span className="t-text-white">{t('employee.label_department')}</span>}
                    name="department_id"
                >
                    <Select
                        placeholder={t('employee.placeholder_department')}
                        allowClear
                        showSearch
                        filterOption={(input, option) => {
                            const removeAccents = (str: string) =>
                                str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/đ/g, "d").replace(/Đ/g, "D");
                            return removeAccents((option?.label as string) || "").toLowerCase().includes(removeAccents(input).toLowerCase());
                        }}
                        className="t-input"
                        options={departments.map(d => ({ value: d.id, label: d.name }))}
                    />
                </Form.Item>

                <Form.Item
                    label={<span className="t-text-white">Nhóm quyền</span>}
                    name="role_id"
                >
                    <Select
                        placeholder="Chọn nhóm quyền"
                        allowClear
                        showSearch
                        filterOption={(input, option) => {
                            const removeAccents = (str: string) =>
                                str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/đ/g, "d").replace(/Đ/g, "D");
                            return removeAccents((option?.label as string) || "").toLowerCase().includes(removeAccents(input).toLowerCase());
                        }}
                        className="t-input"
                        options={roles.map(r => ({ value: r.id, label: r.name }))}
                    />
                </Form.Item>

                <Form.Item
                    label={<span className="t-text-white">Email đăng nhập</span>}
                    name="email_user"
                    rules={[
                        { type: "email", message: "Email không hợp lệ" },
                    ]}
                >
                    <Input
                        placeholder="Nhập email đăng nhập"
                        className="dark:bg-[#141414] t-text-white dark:border-gray-600"
                        autoComplete="off"
                    />
                </Form.Item>

                <Form.Item
                    label={<span className="t-text-white">Mật khẩu đăng nhập</span>}
                    name="password_user"
                    rules={[
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                const emailUser = getFieldValue("email_user");
                                // Chỉ bắt buộc khi tạo mới tài khoản. Khi sửa: để trống = không đổi mật khẩu.
                                if (mode === "add" && emailUser && !value) {
                                    return Promise.reject(new Error("Vui lòng nhập mật khẩu"));
                                }
                                if (value && value.length < 6) {
                                    return Promise.reject(new Error("Mật khẩu tối thiểu 6 ký tự"));
                                }
                                return Promise.resolve();
                            },
                        }),
                    ]}
                >
                    <Input.Password
                        placeholder={mode === "edit" ? "Để trống nếu không đổi mật khẩu" : "Nhập mật khẩu"}
                        className="dark:bg-[#141414] t-text-white dark:border-gray-600"
                        autoComplete="new-password"
                    />
                </Form.Item>

                <Form.Item
                    label={<span className="t-text-white">{t('employee.active_status')}</span>}
                    name="is_active"
                    valuePropName="checked"
                >
                    <Switch
                        className="[&.ant-switch-checked]:!bg-[#3e91ab]"
                    />
                </Form.Item>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t dark:border-gray-700">
                    <Button onClick={onCancel}>{t('employee.cancel')}</Button>
                    <Button
                        htmlType="submit"
                        type="primary"
                        loading={loading}
                        className="!bg-[#3e91ab] hover:!bg-[#357f96] border-none"
                    >
                        OK
                    </Button>
                </div>
            </Form>
        </Modal>
    );
}